import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportsService, LateSummaryRow, LateSummaryReportFilters } from '../../services/api/reports';
import { departmentsService } from '../../services/api/departments';
import { Department } from '../../types';
import './LateSummaryReport.css';

const LateSummaryReport: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<LateSummaryRow[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [filters, setFilters] = useState<LateSummaryReportFilters>({
    month: new Date().toISOString().slice(0, 7),
    includeSubtree: false,
  });

  useEffect(() => {
    loadDepartments();
    loadReport();
  }, []);

  useEffect(() => {
    loadReport();
  }, [filters]);

  const loadDepartments = async () => {
    try {
      const depts = await departmentsService.getAll();
      setDepartments(depts);
    } catch (error) {
      console.error('Failed to load departments:', error);
    }
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const result = await reportsService.getLateSummaryReport(filters);
      setData(result);
    } catch (error) {
      console.error('Failed to load late summary report:', error);
      alert('Failed to load report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof LateSummaryReportFilters, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      await reportsService.exportLateSummaryReport(filters);
    } catch (error) {
      console.error('Failed to export report:', error);
      alert('Failed to export report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const totalMinutes = data.reduce((sum, row) => sum + row.minutesApproved, 0);
  const totalOverQuota = data.reduce((sum, row) => sum + row.overQuotaCount, 0);
  const avgUsage = data.length > 0
    ? data.reduce((sum, row) => sum + row.usagePercentage, 0) / data.length
    : 0;

  return (
    <div className="late-summary-report">
      <div className="report-header">
        <button className="back-button" onClick={() => navigate('/reports')}>
          ← Back to Reports
        </button>
        <div>
          <h1>Late/Early Summary (RPT-LATE-01)</h1>
          <p>View approved minutes, quota usage, and over-quota statistics</p>
        </div>
      </div>

      <div className="report-filters">
        <div className="filter-group">
          <label>Month</label>
          <input
            type="month"
            value={filters.month || ''}
            onChange={(e) => handleFilterChange('month', e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>Department</label>
          <select
            value={filters.departmentId || ''}
            onChange={(e) =>
              handleFilterChange('departmentId', e.target.value ? Number(e.target.value) : undefined)
            }
          >
            <option value="">All Departments</option>
            {departments.map((dept) => (
              <option key={dept.id} value={dept.id}>
                {dept.name}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>
            <input
              type="checkbox"
              checked={filters.includeSubtree || false}
              onChange={(e) => handleFilterChange('includeSubtree', e.target.checked)}
            />
            Include Sub-departments
          </label>
        </div>
        <button className="export-button" onClick={handleExport} disabled={loading}>
          {loading ? 'Exporting...' : 'Export Excel'}
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading report...</div>
      ) : (
        <>
          <div className="summary-cards">
            <div className="summary-card">
              <div className="summary-label">Total Minutes</div>
              <div className="summary-value">{totalMinutes}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Over Quota Count</div>
              <div className="summary-value">{totalOverQuota}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Avg Usage %</div>
              <div className="summary-value">{avgUsage.toFixed(1)}%</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Total Employees</div>
              <div className="summary-value">{data.length}</div>
            </div>
          </div>

          <div className="report-table-container">
            <table className="report-table">
              <thead>
                <tr>
                  <th>Employee Code</th>
                  <th>Employee Name</th>
                  <th>Department</th>
                  <th>Minutes Approved</th>
                  <th>Quota Minutes</th>
                  <th>Usage %</th>
                  <th>Over Quota</th>
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="no-data">
                      No data available
                    </td>
                  </tr>
                ) : (
                  data.map((row) => (
                    <tr key={row.employeeId}>
                      <td>{row.employeeCode}</td>
                      <td>{row.employeeName}</td>
                      <td>{row.departmentName}</td>
                      <td className="number-cell">{row.minutesApproved}</td>
                      <td className="number-cell">{row.quotaMinutes}</td>
                      <td className="number-cell">
                        <span className={row.usagePercentage > 100 ? 'over-quota' : ''}>
                          {row.usagePercentage.toFixed(1)}%
                        </span>
                      </td>
                      <td className="number-cell">{row.overQuotaCount}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default LateSummaryReport;
